import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export interface BodyTemplateMeta {
  source?: 'entity' | 'static';
  format?: string;
}

export interface KeyValue {
  key: string;
  value: string;
  meta?: BodyTemplateMeta;
}

export interface ActionConfig {
  id?: string;
  capabilityCode?: string;
  name: string;
  description?: string;
  url: string;
  method: HttpMethod;
  headers: KeyValue[];
  queryParams: KeyValue[];

  // 🔥 NEW
  bodyTemplateWithMetaData?: any;

  // 🧓 OLD (unchanged)
  bodyTemplate?: KeyValue[];
}

const buildBodyTemplateFromCapability = (capabilityDetails: any): KeyValue[] => {
  const params = capabilityDetails?.config?.action?.parameters;
  if (!Array.isArray(params)) return [];

  return params.map((p: any) => {
    let valueType: FieldValueType = 'text';
    let format: string | undefined;

    if (p.format?.includes('YYYY')) {
      valueType = 'date';
      format = 'YYYY-MM-DD';
    } else if (p.format?.includes('HH')) {
      valueType = 'time';
      format = 'HH:mm';
    } else if (p.type === 'number' || p.type === 'integer') {
      valueType = 'number';
    }

    return {
      key: p.name,
      value: `{{${p.name}}}`,
      meta: {
        valueType,
        format,
      },
    };
  });
};

export type FieldValueType =
  | 'text'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'daterange'
  | 'timerange'
  | 'datetimerange'
  | 'single-select'
  | 'multi-select'
  | 'duration';

export interface BodyTemplateMeta {
  source?: 'entity' | 'static';
  format?: string;          // Expected API format
  valueType?: FieldValueType;
  options?: string[];       // For select types
}

export interface KeyValue {
  key: string;
  value: string;
  meta?: BodyTemplateMeta;
}

const VALUE_TYPE_OPTIONS: { label: string; value: FieldValueType }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Time', value: 'time' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Date Range', value: 'daterange' },
  { label: 'Time Range', value: 'timerange' },
  { label: 'DateTime Range', value: 'datetimerange' },
  { label: 'Single Select', value: 'single-select' },
  { label: 'Multi Select', value: 'multi-select' },
  { label: 'Duration (e.g. 1 hour, 30 min)', value: 'duration' },
];

export const DATE_FORMATS = [
  { label: 'YYYY-MM-DD (2026-01-29)', value: 'YYYY-MM-DD' },
  { label: 'DD-MM-YYYY (29-01-2026)', value: 'DD-MM-YYYY' },
  { label: 'MM-DD-YYYY (01-29-2026)', value: 'MM-DD-YYYY' },
  { label: 'MM/DD/YYYY (01/29/2026)', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY (29/01/2026)', value: 'DD/MM/YYYY' },
  { label: 'YYYY/MM/DD (2026/01/29)', value: 'YYYY/MM/DD' },
  { label: 'DD MMM YYYY (29 Jan 2026)', value: 'DD MMM YYYY' },
  { label: 'MMM DD, YYYY (Jan 29, 2026)', value: 'MMM DD, YYYY' },
  { label: 'MMMM DD, YYYY (January 29, 2026)', value: 'MMMM DD, YYYY' },
  { label: 'DD MMMM YYYY (29 January 2026)', value: 'DD MMMM YYYY' },
  { label: 'ddd, DD MMM YYYY (Thu, 29 Jan 2026)', value: 'ddd, DD MMM YYYY' },
  { label: 'dddd, DD MMMM YYYY (Thursday, 29 January 2026)', value: 'dddd, DD MMMM YYYY' },
];

export const TIME_FORMATS = [
  { label: 'HH:mm (14:30)', value: 'HH:mm' },
  { label: 'HH:mm:ss (14:30:45)', value: 'HH:mm:ss' },
  { label: 'hh:mm A (02:30 PM)', value: 'hh:mm A' },
  { label: 'hh:mm:ss A (02:30:45 PM)', value: 'hh:mm:ss A' },
  { label: 'HHmm (1430)', value: 'HHmm' },
];

export const DATETIME_FORMATS = [
  { label: 'YYYY-MM-DD HH:mm', value: 'YYYY-MM-DD HH:mm' },
  { label: 'YYYY-MM-DD HH:mm:ss', value: 'YYYY-MM-DD HH:mm:ss' },
  { label: 'DD-MM-YYYY HH:mm', value: 'DD-MM-YYYY HH:mm' },
  { label: 'DD/MM/YYYY HH:mm', value: 'DD/MM/YYYY HH:mm' },
  { label: 'MM/DD/YYYY hh:mm A', value: 'MM/DD/YYYY hh:mm A' },
  { label: 'DD MMM YYYY hh:mm A (29 Jan 2026 02:30 PM)', value: 'DD MMM YYYY hh:mm A' },
  { label: 'MMM DD, YYYY hh:mm A (Jan 29, 2026 02:30 PM)', value: 'MMM DD, YYYY hh:mm A' },
  { label: 'YYYY-MM-DDTHH:mm:ss (ISO Local)', value: 'YYYY-MM-DDTHH:mm:ss' },
  { label: 'YYYY-MM-DDTHH:mm:ssZ (ISO UTC)', value: 'YYYY-MM-DDTHH:mm:ss[Z]' },
];

export const DURATION_FORMATS = [
  { label: 'Seconds (3600)', value: 'seconds' },
  { label: 'Minutes (60)', value: 'minutes' },
  { label: 'Hours (24)', value: 'hours' },
  { label: 'HH:mm (01:00)', value: 'HH:mm' },
  // { label: 'ISO 8601 (PT1H30M)', value: 'iso8601' },
];

interface ActionConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialConfig?: Partial<ActionConfig>;
  onSubmit: (config: ActionConfig) => void;
  onTest?: (config: ActionConfig) => Promise<void> | void;
  capabilityCode?: string;
  capabilityDetails: any;
}

export const ActionConfigModal: React.FC<ActionConfigModalProps> = ({
  isOpen,
  onClose,
  initialConfig,
  onSubmit,
  onTest,
  capabilityCode,
  capabilityDetails
}) => {
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [method, setMethod] = useState<HttpMethod>('POST');
  const [headers, setHeaders] = useState<KeyValue[]>([]);
  const [queryParams, setQueryParams] = useState<KeyValue[]>([]);
  const [bodyTemplateWithMetaData, setBodyTemplateWithMetaData] =
    useState<KeyValue[]>([]);

  const [bodyTemplate, setBodyTemplate] =
    useState<KeyValue[]>([]);

  const [testing, setTesting] = useState(false);

  useEffect(() => {
    console.log("capabilityDetails==>", JSON.stringify(capabilityDetails))
  }, [capabilityDetails])

  useEffect(() => {
    if (!isOpen) return;

    if (initialConfig) {
      setName(initialConfig.name || '');
      setDescription(initialConfig.description || '');
      setUrl(initialConfig.url || '');
      setMethod(initialConfig.method || 'POST');
      setHeaders(initialConfig.headers || []);
      setQueryParams(initialConfig.queryParams || []);

      // 🔥 NEW default-capability flow
      setBodyTemplateWithMetaData(
        initialConfig.bodyTemplateWithMetaData?.body?.length
          ? initialConfig.bodyTemplateWithMetaData?.body
          : buildBodyTemplateFromCapability(capabilityDetails)
      );

      // 🧓 OLD flow untouched
      setBodyTemplate(initialConfig.bodyTemplate || []);
    } else {
      setName('');
      setDescription('');
      setUrl('');
      setMethod('POST');
      setHeaders([]);
      setQueryParams([]);

      setBodyTemplateWithMetaData(
        buildBodyTemplateFromCapability(capabilityDetails)
      );

      setBodyTemplate([]);
    }
  }, [isOpen, initialConfig, capabilityDetails]);



  // useEffect(() => {
  //   if (isOpen) {
  //     if (initialConfig) {
  //       setName(initialConfig.name || '');
  //       setDescription(initialConfig.description || '');
  //       setUrl(initialConfig.url || '');
  //       setMethod(initialConfig.method || 'POST');
  //       setHeaders(initialConfig.headers || []);
  //       setQueryParams(initialConfig.queryParams || []);
  //       setBodyTemplate(initialConfig.bodyTemplate || []);
  //     } else {
  //       resetForm();
  //     }
  //   }
  // }, [isOpen, initialConfig]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setUrl('');
    setMethod('POST');
    setHeaders([]);
    setQueryParams([]);
    setBodyTemplate([]);
  };

  const updateRow = (
    idx: number,
    field: 'key' | 'value',
    value: string,
    rows: KeyValue[],
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>
  ) => {
    setter(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const cfg: ActionConfig = {
        name,
        url,
        method,
        headers,
        queryParams,
        description,
        bodyTemplate,
      };

      if (onTest) {
        await onTest(cfg);
      } else {
        const hdrs = headers.reduce<Record<string, string>>((acc, r) => {
          if (r.key) acc[r.key] = r.value ?? '';
          return acc;
        }, {});

        const params = queryParams.reduce<Record<string, string>>((acc, r) => {
          if (r.key) acc[r.key] = r.value ?? '';
          return acc;
        }, {});

        const requestUrl = new URL(url);
        Object.entries(params).forEach(([k, v]) => {
          requestUrl.searchParams.set(k, v);
        });

        const requestBody =
          method === 'GET'
            ? undefined
            : bodyTemplate.reduce<Record<string, string>>((acc, r) => {
              if (r.key) acc[r.key] = r.value ?? '';
              return acc;
            }, {});

        const resp = await fetch(requestUrl.toString(), {
          method,
          headers: { 'Content-Type': 'application/json', ...hdrs },
          body: JSON.stringify(requestBody),
        });

        toast.success(`Test completed: ${resp.status} ${resp.statusText}`);
      }
    } catch (e) {
      toast.error('Test failed. Check console for details.');
      console.error('ActionConfigModal Test error:', e);
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = () => {

    console.log("bodytemplate==>", bodyTemplate)

    if (!name.trim() || !url.trim()) {
      toast.error('Name and URL are required');
      return;
    }

    const requiredParams =
      capabilityDetails?.config?.action?.parameters?.filter((p: any) => p.required) || [];

    for (const p of requiredParams) {
      const row = bodyTemplateWithMetaData.find(r => r.key === p.name);
      if (!row || !row.value?.trim()) {
        toast.error(`Required field "${p.name}" is missing`);
        return;
      }
    }

    const cfg: ActionConfig = {
      name,
      url,
      method,
      headers: headers.filter(r => r.key.trim()),
      queryParams: queryParams.filter(r => r.key.trim()),
      description,
      bodyTemplateWithMetaData: { "body": bodyTemplateWithMetaData.filter(r => r.key.trim()) },
      bodyTemplate: bodyTemplate.filter(r => r.key.trim()),

    };

    onSubmit(cfg);
    resetForm();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-agentic-text-primary">
            Configure Action{capabilityCode && `: ${capabilityCode}`}
          </h3>
          <button
            className="p-1.5 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Action name"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                Method
              </label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as HttpMethod)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              API URL
            </label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.example.com/endpoint"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this action..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
            />
          </div>

          {/* Headers */}
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-agentic-text-primary">Header Fields</div>
              <button
                type="button"
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
                onClick={() => setHeaders((r) => [...r, { key: '', value: '' }])}
              >
                + Add Header
              </button>
            </div>
            <div className="space-y-2">
              {headers.map((row, idx) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2">
                  <button
                    type="button"
                    className="self-start md:self-auto px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-red-500"
                    aria-label="Remove row"
                    onClick={() => setHeaders((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </button>
                  <input
                    value={row.key}
                    onChange={(e) => updateRow(idx, 'key', e.target.value, headers, setHeaders)}
                    placeholder="Key (e.g., Authorization)"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                  <input
                    value={row.value}
                    onChange={(e) =>
                      updateRow(idx, 'value', e.target.value, headers, setHeaders)
                    }
                    placeholder="Value"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
              ))}
              {headers.length === 0 && (
                <div className="text-sm text-agentic-text-secondary">No headers added.</div>
              )}
              <p className="text-xs text-agentic-text-secondary mt-2">
                💡 Use template expressions like {`{{variable}}`} or static values. Values with template expressions stay as strings, others are JSON-parsed.
              </p>
            </div>
          </div>

          {/* Query Parameters */}
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-agentic-text-primary">Query Parameters</div>
              <button
                type="button"
                className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
                onClick={() => setQueryParams((r) => [...r, { key: '', value: '' }])}
              >
                + Add Param
              </button>
            </div>
            <div className="space-y-2">
              {queryParams.map((row, idx) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2">
                  <button
                    type="button"
                    className="self-start md:self-auto px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-red-500"
                    aria-label="Remove row"
                    onClick={() => setQueryParams((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </button>
                  <input
                    value={row.key}
                    onChange={(e) =>
                      updateRow(idx, 'key', e.target.value, queryParams, setQueryParams)
                    }
                    placeholder="Key"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                  <input
                    value={row.value}
                    onChange={(e) =>
                      updateRow(idx, 'value', e.target.value, queryParams, setQueryParams)
                    }
                    placeholder="Value"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                  />
                </div>
              ))}
              {queryParams.length === 0 && (
                <div className="text-sm text-agentic-text-secondary">No parameters added.</div>
              )}
              <p className="text-xs text-agentic-text-secondary mt-2">
                💡 Use template expressions like {`{{variable}}`} or static values. Values with template expressions stay as strings, others are JSON-parsed.
              </p>
            </div>
          </div>

          {/* Body Template - Same as Headers */}
          {/* Body Template – Capability Driven */}
          {/* Body Template */}
          {method !== 'GET' && (
            <div className="border rounded-xl p-4 space-y-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-agentic-text-primary">
                  Request Body (Conversational Fields)
                </div>

                <button
                  type="button"
                  className="text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                  onClick={() =>
                    setBodyTemplateWithMetaData(prev => [
                      ...prev,
                      { key: '', value: '', meta: { valueType: 'text' } }
                    ])
                  }
                >
                  + Add Field
                </button>
              </div>

              <div className="space-y-3">
                {bodyTemplateWithMetaData.map((row, idx) => (
                  <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2 flex-wrap border-b border-gray-200 pb-4 mb-4">

                    <button
                      type="button"
                      className="self-start md:self-auto px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-red-500"
                      aria-label="Remove row"
                      onClick={() =>
                        setBodyTemplateWithMetaData(prev => prev.filter((_, i) => i !== idx))
                      }
                    >
                      ✕
                    </button>

                    {/* Key */}
                    <input
                      value={row.key}
                      onChange={(e) =>
                        updateRow(idx, 'key', e.target.value, bodyTemplateWithMetaData, setBodyTemplateWithMetaData)
                      }
                      placeholder="Field key"
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                    />

                    {/* Template Value */}
                    <input
                      value={row.value}
                      onChange={(e) =>
                        updateRow(idx, 'value', e.target.value, bodyTemplateWithMetaData, setBodyTemplateWithMetaData)
                      }
                      placeholder="{{variable}} or static"
                      className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                    />

                    {/* Value Type */}
                    <select
                      value={row.meta?.valueType || 'text'}
                      onChange={(e) =>
                        setBodyTemplateWithMetaData(prev =>
                          prev.map((r, i) =>
                            i === idx
                              ? { ...r, meta: { ...(r.meta || {}), valueType: e.target.value as FieldValueType } }
                              : r
                          )
                        )
                      }
                      className="w-full md:w-auto md:max-w-[160px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                    >
                      {VALUE_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>

                    {/* Format / Options */}
                    <div className="col-span-2">
                      {(row.meta?.valueType === 'date') && (
                        <select
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                          <option value="">Select date format</option>
                          {DATE_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      )}

                      {(row.meta?.valueType === 'time') && (
                        <select
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                          <option value="">Select time format</option>
                          {TIME_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      )}

                      {(row.meta?.valueType === 'datetime') && (
                        <select
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                          <option value="">Select datetime format</option>
                          {DATETIME_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      )}

                      {(row.meta?.valueType === 'single-select' || row.meta?.valueType === 'multi-select') && (
                        <input
                          placeholder="Comma separated options (e.g. Morning,Afternoon,Evening)"
                          value={row.meta?.options?.join(',') || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx
                                  ? { ...r, meta: { ...r.meta, options: e.target.value.split(',').map(s => s.trim()) } }
                                  : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        />
                      )}

                      {row.meta?.valueType === 'duration' && (
                        <select
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        >
                          <option value="">Select duration format</option>
                          {DURATION_FORMATS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      )}

                    </div>
                  </div>
                ))}

                {bodyTemplateWithMetaData.length === 0 && (
                  <div className="text-sm text-agentic-text-secondar">No conversational body fields configured.</div>
                )}
              </div>
            </div>
          )}

        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50 disabled:opacity-50"
            onClick={handleTest}
            disabled={testing || !url.trim()}
          >
            {testing ? 'Testing...' : 'Test Config'}
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-white bg-agentic-primary-blue hover:bg-agentic-primary-blue/90"
            onClick={handleSubmit}
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionConfigModal;
